import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight, BookOpen, Bookmark, Calendar, MessageSquare, Sparkles, TrendingUp, GraduationCap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Loading, ResourceCard, TierBadge } from "@/components/shared";
import { TIERS } from "@/lib/constants";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

function StatCard({ icon: Icon, label, value }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5">
      <div className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-emerald/10 text-emerald"><Icon className="h-[18px] w-[18px]" strokeWidth={1.75} /></div>
      <p className="mt-3 font-heading text-2xl font-bold text-navy">{value}</p>
      <p className="text-sm text-slate-500">{label}</p>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { data, isLoading } = useQuery({ queryKey: ["dashboard"], queryFn: async () => (await api.get("/dashboard")).data });

  if (isLoading) return <DashboardLayout><Loading /></DashboardLayout>;

  return (
    <DashboardLayout>
      <div className="mb-8 flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-navy">Welcome back, {user.name.split(" ")[0]}</h1>
          <p className="mt-1 text-slate-600">Here's what's happening in your account.</p>
        </div>
        <div className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3">
          <div><p className="text-xs text-slate-400">Membership</p><TierBadge tier={user.tier} /></div>
          {user.tier !== "pro" && <Button size="sm" onClick={() => navigate("/pricing")} className="rounded-md bg-emerald text-white hover:bg-emerald-700" data-testid="dashboard-upgrade-btn">Upgrade</Button>}
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard icon={Bookmark} label="Saved resources" value={data.saved_resources.length} />
        <StatCard icon={GraduationCap} label="Courses in progress" value={data.continue_learning.length} />
        <StatCard icon={Calendar} label="Upcoming workshops" value={data.upcoming_workshops.length} />
        <StatCard icon={TrendingUp} label="Membership" value={TIERS[user.tier]?.label} />
      </div>

      {data.current_pack && (
        <section className="mt-10">
          <h2 className="font-heading text-xl font-semibold text-navy">Current implementation path</h2>
          <Link to={`/packs/${data.current_pack.slug}`} data-testid="current-pack" className="mt-4 flex items-center justify-between rounded-xl border border-emerald/30 bg-emerald/5 p-5 transition-colors hover:bg-emerald/10">
            <div className="flex-1">
              <p className="font-heading font-semibold text-navy">{data.current_pack.title}</p>
              <div className="mt-2 max-w-md"><Progress value={data.current_pack.total ? Math.round((data.current_pack.done / data.current_pack.total) * 100) : 0} className="h-1.5" /><p className="mt-1.5 text-xs text-slate-500">{data.current_pack.done}/{data.current_pack.total} steps</p></div>
            </div>
            <ArrowRight className="h-5 w-5 text-emerald" />
          </Link>
        </section>
      )}

      {data.continue_learning.length > 0 && (
        <section className="mt-10">
          <h2 className="font-heading text-xl font-semibold text-navy">Continue learning</h2>
          <div className="mt-4 grid gap-4 sm:grid-cols-3">
            {data.continue_learning.map((c) => {
              const pct = c.total ? Math.round((c.done / c.total) * 100) : 0;
              return (
                <Link key={c.id} to={`/courses/${c.slug}`} className="group rounded-xl border border-slate-200 bg-white p-4 transition-colors hover:bg-slate-50" data-testid={`continue-${c.id}`}>
                  <p className="font-heading font-semibold text-navy group-hover:text-emerald">{c.title}</p>
                  <div className="mt-3"><Progress value={pct} className="h-1.5" /><p className="mt-1.5 text-xs text-slate-500">{c.done}/{c.total} lessons</p></div>
                </Link>
              );
            })}
          </div>
        </section>
      )}

      <section className="mt-10">
        <div className="flex items-center justify-between"><h2 className="font-heading text-xl font-semibold text-navy"><Sparkles className="mr-1.5 inline h-5 w-5 text-emerald" /> Recommended for you</h2><Link to="/resources" className="text-sm font-semibold text-emerald hover:underline" data-testid="dash-all-resources">View all →</Link></div>
        <div className="mt-4 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">{data.recommended.map((r) => <ResourceCard key={r.id} r={r} />)}</div>
      </section>

      <div className="mt-10 grid gap-8 lg:grid-cols-3">
        <section className="lg:col-span-2">
          <h2 className="font-heading text-xl font-semibold text-navy"><BookOpen className="mr-1.5 inline h-5 w-5 text-emerald" /> Recently added</h2>
          <div className="mt-4 space-y-3">
            {data.recent_resources.map((r) => (
              <Link key={r.id} to={`/resources/${r.slug}`} className="flex items-center gap-4 rounded-xl border border-slate-200 bg-white p-3 transition-colors hover:bg-slate-50" data-testid={`recent-${r.id}`}>
                {r.cover_image && <img src={r.cover_image} alt="" className="h-14 w-20 rounded-md object-cover" />}
                <div className="min-w-0 flex-1"><p className="truncate font-semibold text-navy">{r.title}</p><p className="text-xs text-slate-400">{r.category} · {r.resource_format}</p></div>
                <TierBadge tier={r.membership_requirement} />
              </Link>
            ))}
          </div>
        </section>
        <section>
          <h2 className="font-heading text-xl font-semibold text-navy"><Calendar className="mr-1.5 inline h-5 w-5 text-emerald" /> Upcoming workshops</h2>
          <div className="mt-4 space-y-3">
            {data.upcoming_workshops.map((w) => (
              <div key={w.id} className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`workshop-${w.id}`}>
                <p className="font-semibold text-navy">{w.title}</p>
                <p className="mt-1 text-xs text-slate-500">{new Date(w.scheduled_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}</p>
              </div>
            ))}
          </div>
          <h2 className="mt-8 font-heading text-xl font-semibold text-navy"><MessageSquare className="mr-1.5 inline h-5 w-5 text-emerald" /> Community activity</h2>
          <div className="mt-4 space-y-2">
            {data.community_activity.map((p) => (
              <Link key={p.id} to={`/community/post/${p.id}`} className="block rounded-lg border border-slate-200 bg-white p-3 text-sm transition-colors hover:bg-slate-50" data-testid={`activity-${p.id}`}>
                <p className="truncate font-semibold text-navy">{p.title}</p>
                <p className="text-xs text-slate-400">{p.author_name}</p>
              </Link>
            ))}
          </div>
        </section>
      </div>
    </DashboardLayout>
  );
}
